/**
 * Line Configuration
 * 
 * 統一路線配置
 * 包含路線名稱、顏色等資訊
 */
export const LINE_CONFIG = {
    EAL: {
        name: { tc: '東鐵線', en: 'East Rail Line' },
        color: '#5eb6e4',
        darkColor: '#7dd3fc' // sky-300
    },
    TCL: {
        name: { tc: '東涌線', en: 'Tung Chung Line' },
        color: '#fe7f1d',
        darkColor: '#fb923c' // orange-400
    },
    TKL: {
        name: { tc: '將軍澳線', en: 'Tseung Kwan O Line' },
        color: '#6b208b',
        darkColor: '#c084fc' // purple-400
    },
    AEL: {
        name: { tc: '機場快線', en: 'Airport Express' },
        color: '#1c7670',
        darkColor: '#2dd4bf' // teal-400
    },
    TML: {
        name: { tc: '屯馬線', en: 'Tuen Ma Line' },
        color: '#9a3b26',
        darkColor: '#f87171' // red-400
    },
    SIL: {
        name: { tc: '南港島線', en: 'South Island Line' },
        color: '#b5bd00',
        darkColor: '#d9f99d' // lime-200
    },
    TWL: {
        name: { tc: '荃灣線', en: 'Tsuen Wan Line' },
        color: '#ed1d24',
        darkColor: '#fb7185' // rose-400
    },
    ISL: {
        name: { tc: '港島線', en: 'Island Line' },
        color: '#0860a8',
        darkColor: '#60a5fa' // blue-400
    },
    KTL: {
        name: { tc: '觀塘線', en: 'Kwun Tong Line' },
        color: '#1a9431',
        darkColor: '#4ade80' // green-400
    },
    DRL: {
        name: { tc: '迪士尼線', en: 'Disneyland Resort Line' },
        color: '#f550a6',
        darkColor: '#f472b6' // pink-400
    },
    LRT: {
        name: { tc: '輕鐵', en: 'Light Rail' },
        color: '#02077b',
        darkColor: '#5c64ff' // brighter indigo for dark mode contrast
    },
    MTRBus: {
        name: { tc: '港鐵巴士', en: 'MTR Bus' },
        color: '#000080',
        darkColor: '#3b82f6' // brighter blue for dark mode contrast
    },
};

/**
 * 獲取路線配置
 * @param {string} lineCode - 路線代碼
 * @returns {object|null} 路線配置或 null
 */
export const getLineConfig = (lineCode) => {
    return LINE_CONFIG[lineCode] || null;
};

/**
 * 獲取路線名稱
 * @param {string} lineCode - 路線代碼
 * @param {string} language - 語言 ('tc' 或 'en')
 * @returns {string} 路線名稱
 */
export const getLineName = (lineCode, language = 'tc') => {
    const config = getLineConfig(lineCode);
    return config ? config.name[language] || config.name.tc : lineCode;
};

/**
 * 獲取路線顏色
 * @param {string} lineCode - 路線代碼
 * @param {string} mode - 主題模式 ('light' 或 'dark')
 * @returns {string} 路線顏色
 */
export const getLineColor = (lineCode, mode = 'light') => {
    const config = getLineConfig(lineCode);
    if (!config) return '#666666';
    return mode === 'dark' ? config.darkColor || config.color : config.color;
};

export const MTR_ROUTE_MAP_LAYOUTS = {
    // --- East Rail Line (EAL) ---
    EAL: {
        width: 400,
        height: 1000,
        paths: [
            { d: "M 140 60 Q 140 100 170 120 L 200 140" },
            { d: "M 260 60 Q 260 100 230 120 L 200 140" },
            { d: "M 200 140 L 200 920" },
            { d: "M 200 400 Q 280 400 280 460 Q 280 520 200 520", type: 'dashed', strokeWidth: 8 }
        ],
        stations: {
            LOW: { x: 140, y: 60, align: 'left' },
            LMC: { x: 260, y: 60, align: 'right' },
            SHS: { x: 200, y: 140, align: 'left', isPill: true },
            FAN: { x: 200, y: 200, align: 'left' },
            TWO: { x: 200, y: 260, align: 'left' },
            TAP: { x: 200, y: 320, align: 'left' },
            UNI: { x: 200, y: 380, align: 'left' },
            FOT: { x: 200, y: 460, align: 'left' },
            RAC: { x: 280, y: 460, align: 'right' },
            SHT: { x: 200, y: 540, align: 'left' },
            TAW: { x: 200, y: 600, align: 'left', interchange: ['TML'] },
            KOT: { x: 200, y: 660, align: 'left', interchange: ['KTL'] },
            MKK: { x: 200, y: 720, align: 'left' },
            HUH: { x: 200, y: 780, align: 'left', interchange: ['TML'] },
            EXC: { x: 200, y: 840, align: 'left' },
            ADM: { x: 200, y: 920, align: 'left', interchange: ['TWL', 'ISL', 'SIL'], isTerminal: true },
        }
    },

    // --- Tsuen Wan Line (TWL) ---
    TWL: {
        width: 400,
        height: 1000,
        paths: [
            { d: "M 200 60 L 200 960" }
        ],
        stations: {
            TSW: { x: 200, y: 60, align: 'left' },
            TWH: { x: 200, y: 120, align: 'left' },
            KWH: { x: 200, y: 180, align: 'left' },
            KWF: { x: 200, y: 240, align: 'left' },
            LAK: { x: 200, y: 300, align: 'left', interchange: ['TCL'] },
            MEF: { x: 200, y: 360, align: 'left', interchange: ['TML'] },
            LCK: { x: 200, y: 420, align: 'left' },
            CSW: { x: 200, y: 480, align: 'left' },
            SSP: { x: 200, y: 540, align: 'left' },
            PRE: { x: 200, y: 600, align: 'left', interchange: ['KTL'] },
            MOK: { x: 200, y: 660, align: 'left', interchange: ['KTL'] },
            YMT: { x: 200, y: 720, align: 'left', interchange: ['KTL'] },
            JOR: { x: 200, y: 780, align: 'left' },
            TST: { x: 200, y: 840, align: 'left' },
            ADM: { x: 200, y: 900, align: 'left', interchange: ['EAL', 'ISL', 'SIL'] },
            CEN: { x: 200, y: 960, align: 'left', interchange: ['ISL'], isTerminal: true },
        }
    },

    // --- Island Line (ISL) --- VERTICAL Layout (Reversed: Chai Wan -> Kennedy Town)
    ISL: {
        width: 400,
        height: 1080,
        paths: [
            { d: "M 200 60 L 200 1040" } // Main Line
        ],
        stations: {
            CHW: { x: 200, y: 60, align: 'left' },
            HFC: { x: 200, y: 120, align: 'left' },
            SKW: { x: 200, y: 180, align: 'left' },
            SWH: { x: 200, y: 240, align: 'left' },
            TAK: { x: 200, y: 300, align: 'left' },
            QUB: { x: 200, y: 360, align: 'left', interchange: ['TKL'] },
            NOP: { x: 200, y: 420, align: 'left', interchange: ['TKL'] },
            FOH: { x: 200, y: 480, align: 'left' },
            TIH: { x: 200, y: 540, align: 'left' },
            CAB: { x: 200, y: 600, align: 'left' },
            WAC: { x: 200, y: 660, align: 'left' },
            ADM: { x: 200, y: 720, align: 'left', interchange: ['TWL', 'EAL', 'SIL'], isPill: true },
            CEN: { x: 200, y: 800, align: 'left', interchange: ['TWL', 'TCL', 'AEL'] },
            SHW: { x: 200, y: 860, align: 'left' },
            SYP: { x: 200, y: 920, align: 'left' },
            HKU: { x: 200, y: 980, align: 'left', interchange: ['SIL'] },
            KET: { x: 200, y: 1040, align: 'left' }, // This station is outside the 1000 height, but keeping it for now.
        }
    },

    // --- Kwun Tong Line (KTL) ---
    KTL: {
        width: 400,
        height: 1100,
        paths: [{ d: "M 200 60 L 200 960" }],
        stations: {
            WHA: { x: 200, y: 60, align: 'left' },
            HOM: { x: 200, y: 120, align: 'left', interchange: ['TML'] },
            YMT: { x: 200, y: 180, align: 'left', interchange: ['TWL'] },
            MOK: { x: 200, y: 240, align: 'left', interchange: ['TWL'] },
            PRE: { x: 200, y: 300, align: 'left', interchange: ['TWL'] },
            SKM: { x: 200, y: 360, align: 'left' },
            LOF: { x: 200, y: 420, align: 'left' },
            WTS: { x: 200, y: 480, align: 'left' },
            DIH: { x: 200, y: 540, align: 'left', interchange: ['TML'] },
            CHH: { x: 200, y: 600, align: 'left' },
            KOB: { x: 200, y: 660, align: 'left' },
            NTK: { x: 200, y: 720, align: 'left' },
            KWT: { x: 200, y: 780, align: 'left' },
            LAT: { x: 200, y: 840, align: 'left' },
            YAT: { x: 200, y: 900, align: 'left', interchange: ['TKL'] },
            TIK: { x: 200, y: 960, align: 'left', interchange: ['TKL'] },
        }
    },

    // --- Tseung Kwan O Line (TKL) --- Y-Shape (Inverted: Branches at Top -> North Point at Bottom)
    TKL: {
        width: 400,
        height: 800,
        paths: [
            { d: "M 140 60 L 140 180 L 200 240" }, // Po Lam Branch -> TKO
            { d: "M 260 60 L 260 180 L 200 240" }, // LOHAS Park Branch -> TKO
            { d: "M 200 240 L 200 640" }           // TKO -> North Point
        ],
        stations: {
            POA: { x: 140, y: 60, align: 'left' },
            HAH: { x: 140, y: 140, align: 'left' },
            LHP: { x: 260, y: 60, align: 'right' },
            TKO: { x: 200, y: 240, align: 'left', isPill: true },
            TIK: { x: 200, y: 320, align: 'left', interchange: ['KTL'] },
            YAT: { x: 200, y: 400, align: 'left', interchange: ['KTL'] },
            QUB: { x: 200, y: 560, align: 'left', interchange: ['ISL'] },
            NOP: { x: 200, y: 640, align: 'left', interchange: ['ISL'] },
        }
    },

    // --- South Island Line (SIL) ---
    SIL: {
        width: 400,
        height: 400,
        paths: [{ d: "M 200 60 L 200 300" }],
        stations: {
            ADM: { x: 200, y: 60, align: 'left', interchange: ['TWL', 'ISL', 'EAL'] },
            OCP: { x: 200, y: 120, align: 'left' },
            WCH: { x: 200, y: 180, align: 'left' },
            LET: { x: 200, y: 240, align: 'left' },
            SOH: { x: 200, y: 300, align: 'left' },
        }
    },

    // --- Disneyland Resort Line (DRL) ---
    DRL: {
        width: 400,
        height: 300,
        paths: [{ d: "M 200 60 L 200 120" }],
        stations: {
            SUN: { x: 200, y: 60, align: 'left', interchange: ['TCL'] },
            DIS: { x: 200, y: 120, align: 'left' },
        }
    },

    // --- Tung Chung Line (TCL) ---
    TCL: {
        width: 400,
        height: 600,
        paths: [{ d: "M 200 60 L 200 480" }],
        stations: {
            HOK: { x: 200, y: 60, align: 'left', interchange: ['AEL', 'ISL', 'TWL'] }, // HOK is close to CEN/ADM group
            KOW: { x: 200, y: 120, align: 'left', interchange: ['AEL'] },
            OLY: { x: 200, y: 180, align: 'left' },
            NAC: { x: 200, y: 240, align: 'left' },
            LAK: { x: 200, y: 300, align: 'left', interchange: ['TWL'] },
            TSY: { x: 200, y: 360, align: 'left', interchange: ['AEL'] },
            SUN: { x: 200, y: 420, align: 'left', interchange: ['DRL'] },
            TUC: { x: 200, y: 480, align: 'left' },
        }
    },

    // --- Airport Express (AEL) ---
    AEL: {
        width: 400,
        height: 400,
        paths: [{ d: "M 200 60 L 200 300" }],
        stations: {
            HOK: { x: 200, y: 60, align: 'left', interchange: ['TCL'] },
            KOW: { x: 200, y: 120, align: 'left', interchange: ['TCL'] },
            TSY: { x: 200, y: 180, align: 'left', interchange: ['TCL'] },
            AIR: { x: 200, y: 240, align: 'left' },
            AWE: { x: 200, y: 300, align: 'left' },
        }
    },

    // --- Tuen Ma Line (TML) --- Very Long
    TML: {
        width: 400,
        height: 1700,
        paths: [{ d: "M 200 60 L 200 1500" }],
        stations: {
            WKS: { x: 200, y: 60, align: 'left' },
            MOS: { x: 200, y: 120, align: 'left' },
            HEO: { x: 200, y: 180, align: 'left' },
            TSH: { x: 200, y: 240, align: 'left' },
            SHM: { x: 200, y: 300, align: 'left' },
            CIO: { x: 200, y: 360, align: 'left' },
            TAW: { x: 200, y: 420, align: 'left', interchange: ['EAL'] },
            HIK: { x: 200, y: 480, align: 'left' },
            DIH: { x: 200, y: 540, align: 'left', interchange: ['KTL'] },
            KAT: { x: 200, y: 600, align: 'left' },
            SUW: { x: 200, y: 660, align: 'left' },
            TKW: { x: 200, y: 720, align: 'left' },
            HOM: { x: 200, y: 780, align: 'left', interchange: ['KTL'] },
            HUH: { x: 200, y: 840, align: 'left', interchange: ['EAL'] },
            ETS: { x: 200, y: 900, align: 'left' },
            AUS: { x: 200, y: 960, align: 'left' },
            NAC: { x: 200, y: 1020, align: 'left', interchange: ['TCL'] }, // Nam Cheong
            MEF: { x: 200, y: 1080, align: 'left', interchange: ['TWL'] },
            TWW: { x: 200, y: 1140, align: 'left' },
            KSR: { x: 200, y: 1200, align: 'left' },
            YUL: { x: 200, y: 1260, align: 'left' }, // Yuen Long (LRT) - just map logic for now
            LOP: { x: 200, y: 1320, align: 'left' },
            TIS: { x: 200, y: 1380, align: 'left' },
            SIH: { x: 200, y: 1440, align: 'left' },
            TUM: { x: 200, y: 1500, align: 'left' },
        }
    }
};
